/**
 * Handles user inactivity detection by monitoring various user interaction events.
 * Triggers a callback function when the user has been inactive for a specified duration.
 * 
 * @class InactivityHandler
 * @example
 * const handler = new InactivityHandler(5000, () => {
 *   console.log('User has been inactive for 5 seconds');
 * });
 * 
 * // Clean up when done
 * handler.dispose();
 */
class InactivityHandler {
  /**
   * Creates an instance of InactivityHandler.
   * Automatically starts monitoring user activity upon instantiation.
   * 
   * @param {number} timeout - The inactivity timeout duration in milliseconds.
   * @param {Function} onInactivity - Callback function to be invoked when inactivity is detected.
   * @memberof InactivityHandler
   */
  constructor(timeout, onInactivity) {
    this.timeout = timeout;
    this.onInactivity = onInactivity;
    this.inactivityTimer = null;
    this.resetTimer = this.resetTimer.bind(this);
    this.startTimer = this.startTimer.bind(this);
    this.clearTimer = this.clearTimer.bind(this);
    this.handleInactivity = this.handleInactivity.bind(this);
    this.addEventListeners();
    this.startTimer();
  }

  /**
   * Attaches event listeners to the window object to monitor user activity.
   * Monitors: mouse movement, keyboard input, mouse clicks, touch events, and scrolling.
   * 
   * @private
   * @memberof InactivityHandler
   */
  addEventListeners() {
    window.addEventListener("mousemove", this.resetTimer);
    window.addEventListener("keydown", this.resetTimer);
    window.addEventListener("mousedown", this.resetTimer);
    window.addEventListener("touchstart", this.resetTimer);
    window.addEventListener("scroll", this.resetTimer);
  }

  /**
   * Removes all event listeners previously attached by addEventListeners.
   * Should be called during cleanup to prevent memory leaks.
   * 
   * @private
   * @memberof InactivityHandler
   */
  removeEventListeners() {
    window.removeEventListener("mousemove", this.resetTimer);
    window.removeEventListener("keydown", this.resetTimer);
    window.removeEventListener("mousedown", this.resetTimer);
    window.removeEventListener("touchstart", this.resetTimer);
    window.removeEventListener("scroll", this.resetTimer);
  }

  /**
   * Starts the inactivity timer.
   * When the timer expires, the handleInactivity method is invoked.
   * 
   * @private
   * @memberof InactivityHandler
   */
  startTimer() {
    this.inactivityTimer = setTimeout(this.handleInactivity, this.timeout);
  }

  /**
   * Resets the inactivity timer.
   * Called whenever user activity is detected.
   * Clears the current timer and starts a new one.
   * 
   * @private
   * @memberof InactivityHandler
   */
  resetTimer() {
    this.clearTimer();
    this.startTimer();
  }

  /**
   * Clears the current inactivity timer if one exists.
   * 
   * @private
   * @memberof InactivityHandler
   */
  clearTimer() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  /**
   * Handles the inactivity event when the timer expires.
   * Invokes the onInactivity callback and restarts the timer.
   * 
   * @private
   * @memberof InactivityHandler
   */
  handleInactivity() {
    this.onInactivity();
    this.startTimer();
  }

  /**
   * Cleans up the InactivityHandler instance.
   * Clears the timer and removes all event listeners.
   * Should be called when the handler is no longer needed to prevent memory leaks.
   * 
   * @memberof InactivityHandler
   */
  dispose() {
    this.clearTimer();
    this.removeEventListeners();
  }
}

export default InactivityHandler;